// src/pages/ContactDetail.tsx
import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonChip,
  IonContent,
  IonHeader,
  IonIcon,
  IonList,
  IonPage,
  IonSkeletonText,
  IonSpinner,
  IonTitle,
  IonToolbar,
  useIonToast
} from '@ionic/react';
import {
  businessOutline,
  calendarOutline,
  cashOutline,
  documentTextOutline,
  locationOutline,
  mailOpenOutline,
  mailOutline,
  mailUnreadOutline,
  personOutline,
  star,
  starOutline,
  timeOutline,
} from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { enhancedContacts } from '../services/api.ts';
import { extractErrorMessage } from '../utils/errorUtils.ts';
import './ContactDetail.css';

// Interface for Enhanced Contact
interface EnhancedContact {
  id: string;
  name: string;
  title: string | null;
  company: string | null;
  location: string | null;
  type: 'recruiter' | 'connection';
  email: string | null;
  link: string | null;
  last_interaction: string | null;
  total_emails: number;
  emails_received: number;
  emails_sent: number;
  response_rate: number;
  interest_level: number;
  notes: string | null;
}

// Interface for Contact Job
interface ContactJob {
  id: string;
  title: string;
  location: string;
  company_name: string | null;
  posted_date: string;
  salary: string | null;
  link: string | null;
}

// Helper function to format date
const formatDate = (dateString: string | null): string => {
  if (!dateString) return 'Never';

  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
};

// Interest level stars component
const InterestLevel: React.FC<{ level: number }> = ({ level }) => {
  const maxStars = 5;
  const displayLevel = Math.min(Math.max(Math.round(level / 2), 0), maxStars);

  return (
    <div className="interest-level">
      {[...Array(maxStars)].map((_, i) => (
        <IonIcon
          key={i}
          icon={i < displayLevel ? star : starOutline}
          color={i < displayLevel ? "warning" : "medium"}
          style={{ fontSize: '20px', marginRight: '4px' }}
        />
      ))}
    </div>
  );
};

const ContactDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation<{ from: string }>();
  const history = useHistory();
  const [contact, setContact] = useState<EnhancedContact | null>(null);
  const [jobs, setJobs] = useState<ContactJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [present] = useIonToast();

  const handleBack = () => {
    if (location.state?.from) {
      history.replace(location.state.from);
    } else {
      history.replace('/app/network');
    }
  };

  // Fetch contact and jobs data
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchData = async () => {
      try {
        setLoading(true);
        setJobsLoading(true);

        // Fetch enhanced contact details
        const contactResponse = await enhancedContacts.getEnhancedContactById(id, signal);
        setContact(contactResponse);
        setLoading(false);

        // Fetch contact jobs
        const jobsResponse = await enhancedContacts.getEnhancedContactJobs(id, signal);
        setJobs(jobsResponse);
        setJobsLoading(false);
      } catch (error: any) {
        if (!signal.aborted) {
          // Handle 402 errors specially - don't show toast, let InsufficientBalanceModal handle it
          if (error.response?.status === 402) {
            setLoading(false);
            setJobsLoading(false);
            return;
          }
          
          // Use utility function to safely extract error message
          const errorMessage = extractErrorMessage(error, 'Failed to load contact details');
          
          if (errorMessage) {
            present({
              message: errorMessage,
              duration: 3000,
              position: 'bottom',
              color: 'danger'
            });
          }
          
          setLoading(false);
          setJobsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      controller.abort();
    };
  }, [id, present]);

  // Keeping navigation consistent with current implementation
  const handleViewProfile = () => {
    if (contact?.link) {
      window.open(contact.link, '_blank');
    } else {
      present({
        message: 'No profile link available for this contact',
        duration: 2000,
        position: 'bottom',
        color: 'warning'
      });
    }
  };

  // No longer need this function as we're using routerLink

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={handleBack}>Back</IonButton>
          </IonButtons>
          <IonTitle>Contact Details</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        {loading ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%'
          }}>
            <IonSpinner />
          </div>
        ) : !contact ? (
          <div style={{
            textAlign: 'center',
            color: '#666666',
            padding: '20px'
          }}>
            Contact not found
          </div>
        ) : (
          <div>
            {/* Contact Header Card */}
            <div className="contact-header-card">
              <div className="contact-avatar">
                <div className="avatar-inner">
                  {contact.name ? contact.name[0].toUpperCase() : '?'}
                </div>
              </div>

              <h1 className="contact-name">{contact.name}</h1>

              {contact.title && (
                <p className="contact-subtitle">{contact.title}</p>
              )}

              {contact.company && (
                <div className="contact-company">
                  <IonIcon icon={businessOutline} />
                  <span>{contact.company}</span>
                </div>
              )}

              {contact.location && (
                <div className="contact-location">
                  <IonIcon icon={locationOutline} />
                  <span>{contact.location}</span>
                </div>
              )}

              {contact.type === 'recruiter' && (
                <IonChip color="primary" className="recruiter-chip">
                  Recruiter
                </IonChip>
              )}

              <div className="action-buttons">
                <IonButton
                  expand="block"
                  color="primary"
                  routerLink={`/app/network/contact/${contact.id}/email`}
                >
                  <IonIcon slot="start" icon={mailOutline} />
                  Warm Up Email
                </IonButton>

                {contact.link && (
                  <IonButton
                    expand="block"
                    fill="outline"
                    color="primary"
                    onClick={handleViewProfile}
                  >
                    <IonIcon slot="start" icon={personOutline} />
                    LinkedIn
                  </IonButton>
                )}
              </div>
            </div>

            {/* Relationship Metrics Card */}
            <IonCard className="relationship-card">
              <IonCardHeader>
                <IonCardTitle>Relationship</IonCardTitle>
              </IonCardHeader>

              <IonCardContent>
                <div className="relationship-metrics">
                  <div className="metric">
                    <div className="metric-header">Interest Level</div>
                    <div className="metric-value interest-wrapper">
                      <InterestLevel level={contact.interest_level} />
                    </div>
                  </div>

                  <div className="metric">
                    <div className="metric-header">Last Contact</div>
                    <div className="metric-value">
                      <IonIcon icon={timeOutline} />
                      <span>{formatDate(contact.last_interaction)}</span>
                    </div>
                  </div>

                  <div className="metric">
                    <div className="metric-header">Total Emails</div>
                    <div className="metric-value">
                      <IonIcon icon={mailOutline} />
                      <span>{contact.total_emails}</span>
                    </div>
                  </div>

                  {contact.total_emails > 0 && (
                    <>
                      <div className="metric">
                        <div className="metric-header">Emails Sent</div>
                        <div className="metric-value">
                          <IonIcon icon={mailUnreadOutline} />
                          <span>{contact.emails_sent}</span>
                        </div>
                      </div>

                      <div className="metric">
                        <div className="metric-header">Emails Received</div>
                        <div className="metric-value">
                          <IonIcon icon={mailOpenOutline} />
                          <span>{contact.emails_received}</span>
                        </div>
                      </div>

                      <div className="metric">
                        <div className="metric-header">Response Rate</div>
                        <div className="metric-value">
                          <span>{Math.round(contact.response_rate * 100)}%</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </IonCardContent>
            </IonCard>

            {/* Notes Card */}
            {contact.notes && (
              <IonCard className="notes-card">
                <IonCardHeader>
                  <IonCardTitle>
                    <IonIcon icon={documentTextOutline} />
                    Notes
                  </IonCardTitle>
                </IonCardHeader>

                <IonCardContent>
                  <div className="notes-content">
                    {contact.notes}
                  </div>
                </IonCardContent>
              </IonCard>
            )}

            {/* Related Jobs Section */}
            <div className="jobs-section">
              <h2>Related Jobs</h2>

              {jobsLoading ? (
                <div className="loading-jobs">
                  <IonSkeletonText animated style={{ width: '100%', height: '80px' }} />
                  <IonSkeletonText animated style={{ width: '100%', height: '80px' }} />
                </div>
              ) : jobs.length > 0 ? (
                <IonList className="jobs-list">
                  {jobs.map((job) => (
                    <IonCard key={job.id} className="job-card">
                      <IonCardHeader>
                        <IonCardTitle className="job-title">
                          <a href={job.link || '#'} target="_blank" rel="noopener noreferrer">
                            {job.title}
                          </a>
                        </IonCardTitle>
                        <IonCardSubtitle className="job-company">
                          {job.company_name}
                        </IonCardSubtitle>
                      </IonCardHeader>

                      <IonCardContent>
                        <div className="job-details">
                          <div className="job-detail">
                            <IonIcon icon={locationOutline} />
                            <span>{job.location}</span>
                          </div>

                          <div className="job-detail">
                            <IonIcon icon={calendarOutline} />
                            <span>Posted {job.posted_date}</span>
                          </div>

                          {job.salary && (
                            <div className="job-detail">
                              <IonIcon icon={cashOutline} />
                              <span>{job.salary}</span>
                            </div>
                          )}
                        </div>

                        <IonButton
                          expand="block"
                          className="draft-button"
                          routerLink={`/app/network/contact/${contact.id}/job/${job.id}/email`}
                        >
                          DRAFT REFERRAL REQUEST
                        </IonButton>
                      </IonCardContent>
                    </IonCard>
                  ))}
                </IonList>
              ) : (
                <IonCard className="no-jobs-card">
                  <IonCardContent>
                    <p className="no-jobs-message">No related jobs found for this contact</p>
                  </IonCardContent>
                </IonCard>
              )}
            </div>
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

export default ContactDetail;